import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, AlertTriangle, CheckCircle, Plus, Thermometer } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alerts, setAlerts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterType, setFilterType] = useState(searchParams.get('filter') || 'all');
  const [formData, setFormData] = useState({
    asset_id: '',
    severity: 'Warning',
    title: '',
    description: ''
  });

  useEffect(() => {
    loadData();
    // Update filter if URL parameter changes
    const urlFilter = searchParams.get('filter');
    if (urlFilter) {
      setFilterType(urlFilter);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [alertsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/alerts`, config),
        axios.get(`${API}/assets`, config)
      ]);
      setAlerts(alertsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/alerts`, formData, config);
      toast.success('Alert created successfully');
      setShowCreateModal(false);
      setFormData({ asset_id: '', severity: 'Warning', title: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/alerts/${alertId}/acknowledge`, {}, config);
      toast.success('Alert acknowledged');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const getSeverityColor = (severity) => {
    if (severity === 'Critical') return 'bg-red-500/10 text-red-500 border-red-500';
    if (severity === 'Warning') return 'bg-yellow-500/10 text-yellow-500 border-yellow-500';
    return 'bg-blue-500/10 text-blue-500 border-blue-500';
  };

  // Filter alerts based on type
  const filteredAlerts = alerts.filter(alert => {
    if (filterType === 'temperature') {
      // Check if alert title or description mentions temperature
      const text = `${alert.title} ${alert.description}`.toLowerCase();
      return text.includes('temperature') || text.includes('thermal') || 
             text.includes('hot') || text.includes('cold') || 
             text.includes('°c') || text.includes('heating') || text.includes('cooling');
    }
    return true; // Show all alerts if no filter
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" data-testid="alerts-page">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black mb-6"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
            System Alerts
          </h1>
          {JSON.parse(localStorage.getItem('user') || '{}')?.role !== 'guest' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              data-testid="add-alert-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Alert
            </Button>
          )}
        </div>

        {/* Temperature Filter Banner */}
        {filterType === 'temperature' && (
          <Card className="bg-orange-50 border-orange-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Thermometer className="w-6 h-6 text-orange-600" />
                  <div>
                    <h3 className="text-orange-900 font-semibold">Temperature Risk Alerts</h3>
                    <p className="text-orange-700 text-sm">Showing {filteredAlerts.length} temperature-related alerts from extreme weather conditions</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterType('all');
                    navigate('/alerts');
                  }}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  Clear Filter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {filteredAlerts.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No active alerts</p>
              <p className="text-gray-600 text-sm mt-2">All systems operating normally</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="alerts-list">
            {filteredAlerts.map((alert) => (
              <Card key={alert.id} className="bg-white border-gray-200 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <AlertTriangle className={`w-5 h-5 ${
                          alert.severity === 'Critical' ? 'text-red-500' :
                          alert.severity === 'Warning' ? 'text-yellow-500' : 'text-blue-500'
                        }`} />
                        <h3 className="text-black font-semibold text-lg">{alert.title}</h3>
                        <Badge className={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                      </div>
                      <p className="text-gray-700 mb-3">{alert.description}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(alert.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="ml-4">
                      {alert.status === 'Open' && JSON.parse(localStorage.getItem('user') || '{}')?.role !== 'guest' && (
                        <Button
                          size="sm"
                          onClick={() => acknowledgeAlert(alert.id)}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid={`acknowledge-alert-${alert.id}`}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {alert.status === 'Acknowledged' && (
                        <Badge variant="outline" className="text-gray-500">Acknowledged</Badge>
                      )}
                      {alert.status === 'Open' && JSON.parse(localStorage.getItem('user') || '{}')?.role === 'guest' && (
                        <Badge variant="outline" className="text-gray-500">View Only</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-black">Create New Alert</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAlert} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="alert-asset" className="text-gray-700">Asset</Label>
              <Select value={formData.asset_id} onValueChange={(value) => setFormData({...formData, asset_id: value})} required>
                <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="alert-asset-select">
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert-severity" className="text-gray-700">Severity</Label>
              <Select value={formData.severity} onValueChange={(value) => setFormData({...formData, severity: value})}>
                <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="alert-severity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="Info">Info</SelectItem>
                  <SelectItem value="Warning">Warning</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert-title" className="text-gray-700">Alert Title</Label>
              <Input
                id="alert-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., High Temperature Alert"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="alert-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alert-description" className="text-gray-700">Description</Label>
              <Textarea
                id="alert-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the alert details..."
                required
                rows={4}
                className="bg-white border-gray-300 text-black"
                data-testid="alert-description-input"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="alert-submit-btn"
              >
                Create Alert
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertsPage;
